import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, SimpleGrid } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Button, DECIMAL_PROPS, NumberInput, Select, YesNoRadioButton } from "components";
import { CreditOptionsResponse, CreditResponse } from "types";
import { AppUtils } from "utils";

interface CreditRequestPersonalChildrenProps {
  credit: CreditResponse;
  form: any;
  options: CreditOptionsResponse;
  readOnly?: boolean;
}

interface ChildObject {
  creditRequestId?: number;
  birthYear?: Date,
  craft?: boolean,
  craftAmount?: number;
}
const tempChildObject = {
  creditRequestId: undefined,
  birthYear: undefined,
  craft: undefined,
  craftAmount: undefined
} as ChildObject;

const propertyName = 'children';

const CreditRequestPersonalChildren = ({ credit, form, options, readOnly = false }: CreditRequestPersonalChildrenProps) => {
  const { t } = useTranslation();
  const [visibleFields, setVisibleFields] = useState<string[]>(AppUtils.getVisibleChildrenFields(credit[propertyName]));
  const [supportsChildren, setSupportsChildren] = useState<boolean | undefined>(credit.supportsChildren);

  useEffect(() => {
    const formValues = form.getValues();
    if (Object.keys(formValues).length === 0) {
      return;
    }
    const supports = formValues.supportsChildren;
    if (!supports && childrenForm.values.length > 0) {
      childrenForm.setInitialValues([]);
      childrenForm.reset();
    }
    setSupportsChildren(supports);
  }, [form.getValues().supportsChildren]);

  const updateCredit = useCallback(async (values: any, previous: any) => {
    const prevVisibleFields = AppUtils.getVisibleChildrenFields(previous);
    const newVisibleFields = AppUtils.getVisibleChildrenFields(values);
    AppUtils.clearArrayFieldsWhenUpdating(values, previous, newVisibleFields);
    AppUtils.updateArrayFieldsWhenVisibilityChanges(prevVisibleFields, newVisibleFields, values, childrenForm);

    const tempArray: ChildObject[] = [];
    Object.keys(values).forEach(key => {
      const item: ChildObject = { ...values[key] };
      const isNotEmpty = item.birthYear || item.craft || item.craftAmount;
      if (isNotEmpty) {
        item.creditRequestId = credit.id;
        tempArray.push(item);
      }
    });
    form.setFieldValue(propertyName, tempArray);
    setVisibleFields(newVisibleFields);
  }, []);

  const childrenForm = useForm({
    mode: 'uncontrolled',
    initialValues: credit[propertyName] && credit[propertyName].length > 0 ? credit[propertyName].map(el => ({ ...el })) : [],
    onValuesChange(values, previous) {
      if (values[values.length - 1] !== tempChildObject) {
        updateCredit(values, previous);
      }
    },
  });

  const getChildrenValidYears = () => {
    const birthYear = form.values.birthDate?.getFullYear() ?? (new Date().getFullYear() - 25);
    const currentYear = new Date().getFullYear();
    const minYear = Math.max(birthYear, currentYear - 25);
    return Array.from(
      { length: currentYear - minYear + 1 },
      (_, i) => minYear + i
    );
  }

  const childrenView = useMemo(() => {
    const transformed = childrenForm.getTransformedValues();
    const length = Object.keys(transformed).length;
    return Object.keys(transformed).map((el: any, index: number) => (
      <SimpleGrid cols={4} mt={'sm'} key={el}>
        <Select
          label={t('credit.children.birthYear.label', { number: (index + 1) })}
          data={getChildrenValidYears().map(el => (
            { value: el.toString(), label: el.toString() }
          ))}
          key={childrenForm.key(`${index}.birthYear`)}
          {...childrenForm.getInputProps(`${index}.birthYear`)}
          value={
            childrenForm.getInputProps(`${index}.birthYear`).defaultValue?.toString()
          }
          clearable={true}
          disabled={readOnly}
        />
        <YesNoRadioButton
          label={t('credit.children.craft.label')}
          name={`${index}.craft`}
          form={childrenForm}
          t={t}
          disabled={readOnly}
        />

        {visibleFields.includes(`children.${index}.craftAmount`) ?
          <NumberInput
            label={t('credit.children.craftAmount.label')}
            key={childrenForm.key(`${index}.craftAmount`)}
            {...childrenForm.getInputProps(`${index}.craftAmount`)}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
          : <Box/>
        }
        {length > 0 && !readOnly &&
          <Box style={{ alignSelf: 'center' }}>
            <Button
              variant={'transparent'}
              size={'compact-sm'}
              color={'var(--mantine-color-error)'}
              mt={14}
              onClick={() => AppUtils.removeObject(childrenForm, index, undefined)}
            >
              <IconTrash size={20} />
            </Button>
          </Box>
        }
      </SimpleGrid>
    ))
  }, [childrenForm]);


  const addNewButton = useMemo(() => {
    const transformedLength = Object.keys(childrenForm.getTransformedValues()).length;
    if (transformedLength >= 4) {
      return <></>;
    }
    return (
      <Button
        onClick={() => AppUtils.addNewObject(childrenForm, tempChildObject)}
        size={'compact-xs'}
        variant={'transparent'}
        mt={'md'}
      >
        <>
          <Box mr={4}><IconPlus size={16} /></Box>
          {t('credit.children.add')}
        </>
      </Button>
    )
  }, [form]);

  return (
    <Box mt={'md'} p={'md'}>
      <SimpleGrid cols={4}>
        <Select
          label={t('user.maritalStatus.label')}
          data={options['maritalStatus'].map(el => (
            { value: el, label: t(`user.maritalStatus.options.${el}`) }
          ))}
          key={form.key('maritalStatus')}
          {...form.getInputProps('maritalStatus')}
          clearable={true}
          required={true}
          disabled={readOnly}
        />
        <YesNoRadioButton
          label={t('credit.supportsChildren.label')}
          name={'supportsChildren'}
          form={form}
          t={t}
          disabled={readOnly}
        />
      </SimpleGrid>
      {childrenView}
      {supportsChildren && !readOnly && addNewButton}
    </Box>
  )
}

export default CreditRequestPersonalChildren;